/* FP one-line price fit (2026-10-22). Pairs with snippets/price.liquid + the
 * .fp-price--* rules in base.css.
 *
 * A discounted price reads "₹ 6,499  ₹ 12,999  50% OFF" and must stay on one
 * line on every device. The CSS stops it wrapping; this script makes it fit.
 * If a line is wider than its card, it sets --fp-fit (a font-size multiplier)
 * on the price's host element, just small enough to fit. Cards in the same
 * list (grid, carousel) share the smallest multiplier, so a row stays even.
 * The product page price is fitted on its own.
 *
 * Re-fits when a card resizes (rotation, grid toggle), when cards are added
 * (pagination, recommendations, quick add) or when a price changes (variant
 * pick), and once web fonts have loaded.
 *
 * Loaded on every page from snippets/scripts.liquid.
 */
const SELECTOR = '.fp-price--card, .fp-price--pdp';
const LIST = 'ul, ol, .product-grid, slideshow-slides, .resource-list';
const MIN_FIT = 0.7;

/** @type {Set<Element>} */
const dirty = new Set();
let frame = 0;

/** @param {Element} price */
function host(price) {
  return /** @type {HTMLElement} */ (price.closest('product-price') || price.parentElement);
}

/** @param {Element} price */
function listOf(price) {
  if (price.classList.contains('fp-price--pdp')) return price;
  return price.closest(LIST) || price;
}

/**
 * The multiplier this price line needs to fit (measured at full size), or 1 if it fits.
 * @param {Element} price
 */
function needed(price) {
  const line = /** @type {HTMLElement | null} */ (
    price.querySelector('.price__sale:not(.price__hidden), .price__regular:not(.price__hidden)')
  );
  if (!line || !line.clientWidth) return 1;
  const range = document.createRange();
  range.selectNodeContents(line);
  const natural = range.getBoundingClientRect().width;
  // 1px of slack for sub-pixel rounding.
  return natural > line.clientWidth - 1 ? (line.clientWidth - 1) / natural : 1;
}

/** @param {Element} list */
function fitList(list) {
  const prices = list.matches(SELECTOR) ? [list] : [...list.querySelectorAll(SELECTOR)];
  if (!prices.length) return;
  // Measure everything at full size first, then shrink the whole list once.
  for (const price of prices) host(price).style.removeProperty('--fp-fit');
  let fit = 1;
  for (const price of prices) fit = Math.min(fit, needed(price));
  if (fit === 1) return;
  fit = Math.max(MIN_FIT, Math.floor(fit * 100) / 100);
  for (const price of prices) host(price).style.setProperty('--fp-fit', String(fit));
}

function flush() {
  frame = 0;
  const lists = [...dirty];
  dirty.clear();
  for (const list of lists) if (list.isConnected) fitList(list);
}

/** @param {Element} price */
function schedule(price) {
  dirty.add(listOf(price));
  if (!frame) frame = requestAnimationFrame(flush);
}

const resizes = new ResizeObserver((entries) => {
  for (const entry of entries) {
    // A price swapped out by a variant re-render: stop watching it.
    if (!entry.target.isConnected) resizes.unobserve(entry.target);
    else schedule(entry.target);
  }
});

/** @param {ParentNode} root */
function track(root) {
  const prices = root instanceof Element && root.matches(SELECTOR) ? [root] : [];
  prices.push(...root.querySelectorAll(SELECTOR));
  for (const price of prices) {
    resizes.observe(price);
    schedule(price);
  }
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
    const price = target?.closest(SELECTOR);
    if (price) schedule(price);
    for (const node of mutation.addedNodes) if (node instanceof Element) track(node);
  }
}).observe(document.body, { childList: true, subtree: true });

track(document);
document.fonts?.ready.then(() => track(document));
